import React from 'react';
import { Logo } from './Logo';
import { NavLink } from 'react-router-dom';
import {
    Home,
    Users,
    BarChart3,
    Share2,
    LayoutTemplate,
    Settings,
    FileText,
    History,
    LifeBuoy,
    FileBox,
    ChevronLeft,
    Activity,
    TrendingUp,
    Tag,
    Mail,
    Banknote,
    PanelLeftClose,
    Pin,
    Sparkles,
    CalendarCheck
} from 'lucide-react';
import { useSidebarStore } from '../../stores/sidebarStore';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { useTranslation } from 'react-i18next';

// Helper for conditional class names
function cn(...inputs: (string | undefined | null | false)[]) {
    return twMerge(clsx(inputs));
}

interface SidebarItemProps {
    icon: React.ElementType;
    label: string;
    to: string;
    end?: boolean;
    isCollapsed?: boolean;
}

const SidebarItem: React.FC<SidebarItemProps> = ({ icon: Icon, label, to, end, isCollapsed }) => {
    return (
        <NavLink
            to={to}
            className={() => cn("block focus:outline-none")}
            end={end}
        >
            {({ isActive }) => (
                <button
                    className={cn(
                        "sidebar-nav-item w-full flex items-center transition-all duration-200 p-2 rounded-lg mx-auto", // Added base styles
                        isActive
                            ? "sidebar-nav-item-active bg-accent-primary text-white"
                            : "sidebar-nav-item-inactive text-text-primary hover:bg-neutral-100",
                        isCollapsed ? "justify-center px-0" : "gap-3 px-3"
                    )}
                    title={isCollapsed ? label : undefined}
                >
                    <span className="flex-shrink-0">
                        <Icon className="w-5 h-5" />
                    </span>
                    {!isCollapsed && <span className="text-sm font-medium whitespace-nowrap overflow-hidden transition-all duration-300 opacity-100">{label}</span>}
                </button>
            )}
        </NavLink>
    );
};

export const Sidebar: React.FC = () => {
    const { isCollapsed, toggleSidebar, isDesktopCollapsed, toggleDesktopCollapse } = useSidebarStore();
    const [isHovered, setIsHovered] = React.useState(false);

    // Determine effective display state for content
    const showFullContent = !isDesktopCollapsed || isHovered;
    const isOverlay = isDesktopCollapsed && isHovered;

    const { t } = useTranslation();
    const menuItems = [
        // Overview
        { id: 'dashboard', label: t('sidebar.dashboard'), to: '/', icon: Home, end: true },
        { id: 'analytics', label: t('sidebar.analytics'), to: '/analytics', icon: BarChart3 },
        { id: 'platform-value', label: t('sidebar.platformValue'), to: '/platform-value', icon: TrendingUp },
        // Business Data
        { id: 'customers', label: t('sidebar.customers'), to: '/customers', icon: Users },
        { id: 'orders', label: t('sidebar.orders'), to: '/payments/orders', icon: FileText },
        { id: 'products', label: t('sidebar.products'), to: '/payments/products', icon: FileBox },
        { id: 'discounts', label: t('sidebar.discounts'), to: '/payments/discounts', icon: Tag },
        // Platform
        { id: 'features', label: t('sidebar.features'), to: '/payments/features', icon: Settings },
        { id: 'channels', label: t('sidebar.channels'), to: '/channels', icon: Share2 },
        { id: 'templates', label: t('sidebar.templateStudio'), to: '/templates', icon: LayoutTemplate, end: true },
        { id: 'ai-usage', label: t('sidebar.aiUsage'), to: '/ai-usage', icon: Sparkles },
        { id: 'costs', label: t('sidebar.costs'), to: '/costs', icon: Banknote },
        { id: 'email', label: t('sidebar.emailManager'), to: '/email', icon: Mail },
        { id: 'demo-requests', label: t('sidebar.demoRequests'), to: '/demo-requests', icon: CalendarCheck },
        // System
        { id: 'logs', label: t('sidebar.logs'), to: '/logs', icon: Activity },
        { id: 'audit', label: t('sidebar.auditTrail'), to: '/templates/audit', icon: History },
        { id: 'support', label: t('sidebar.support'), to: '/templates/support', icon: LifeBuoy },
        { id: 'settings', label: t('sidebar.settings'), to: '/settings', icon: Settings },
    ];


    return (
        <>
            <div
                className={cn(
                    "fixed inset-0 bg-neutral-900/50 z-20 md:hidden transition-opacity",
                    isCollapsed ? "opacity-0 pointer-events-none" : "opacity-100"
                )}
                onClick={toggleSidebar}
            />

            <aside
                className={cn(
                    "fixed md:sticky top-0 left-0 h-screen z-30 transition-all duration-300 ease-in-out",
                    isDesktopCollapsed ? "md:w-20" : "md:w-64",
                    isCollapsed ? "-translate-x-full md:translate-x-0" : "translate-x-0",
                    "bg-background-elevated border-r border-neutral-200"
                )}
                onMouseEnter={() => isDesktopCollapsed && setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <div className={cn(
                    "h-full flex flex-col bg-background-elevated transition-all duration-300",
                    isOverlay ? "absolute top-0 left-0 w-64 shadow-xl border-r border-neutral-200 z-50 h-screen" : "w-full"
                )}>
                    <div className={cn(
                        "h-16 flex items-center px-4 border-b border-neutral-200 flex-shrink-0",
                        showFullContent ? "justify-between" : "justify-center"
                    )}>
                        {showFullContent && (
                            <div className="flex items-center gap-2.5 font-medium text-lg text-text-primary px-2 min-w-0">
                                <Logo className="w-6 h-6 flex-shrink-0" />
                                <span className="bg-clip-text text-transparent bg-gradient-to-r from-neutral-800 to-neutral-600 text-sm truncate">
                                    Dashbal Admin
                                </span>
                            </div>
                        )}
                        {!showFullContent && (
                            <Logo className="w-6 h-6 flex-shrink-0" />
                        )}

                        <button
                            onClick={toggleSidebar}
                            className="p-2 rounded-lg hover:bg-neutral-200 ml-auto md:hidden"
                        >
                            <ChevronLeft size={20} />
                        </button>

                        {/* Desktop Collapse/Pin Button */}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleDesktopCollapse();
                                if (isDesktopCollapsed) setIsHovered(false); // Reset hover on pin
                            }}
                            className={cn(
                                "p-1.5 rounded-lg hover:bg-neutral-200 text-neutral-500 transition-colors",
                                showFullContent ? "hidden md:flex" : "hidden"
                            )}
                            title={isDesktopCollapsed ? "Pin Sidebar" : "Collapse Sidebar"}
                        >
                            {isDesktopCollapsed ? <Pin size={18} /> : <PanelLeftClose size={18} />}
                        </button>
                    </div>

                    <nav className="flex-1 p-3 overflow-y-auto overflow-x-hidden">
                        <ul className="space-y-1">
                            {menuItems.map((item) => (
                                <li key={item.id}>
                                    <SidebarItem
                                        icon={item.icon}
                                        label={item.label}
                                        to={item.to}
                                        end={item.end}
                                        isCollapsed={!showFullContent}
                                    />
                                </li>
                            ))}
                        </ul>
                    </nav>
                </div>
            </aside>
        </>
    );
};
